import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api } from "../api";
import {
  Rule,
  Footer,
  Loading,
  Nav,
  ScatterBand,
  Verse,
  ErrorNote,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const fetcher = (url) => api.get(url).then((r) => r.data);

const ICON_CLASS = {
  hand: "ic-hand",
  lattice: "ic-lattice",
  flame: "ic-flame",
  well: "ic-well",
  chalice: "ic-chalice",
};

export default function Visit() {
  useBodyClass("inner");
  useDocumentTitle(
    "What you'd be walking into",
    "Before you come to Sch\u0113ma: what actually happens, what to bring, and whether it's going to be awkward.",
  );

  const { data, error, isLoading, mutate } = useSWR("/visit", fetcher);

  if (isLoading) {
    return (
      <>
        <Nav />
        <Loading label="Opening the door" testid="visit-loading" />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Nav />
        <section>
          <ErrorNote
            message="We couldn't load this page just now."
            onRetry={() => mutate()}
            testid="visit-error"
          />
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Nav />
      <header className="page-head" data-testid="visit-page-head">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>{data.eyebrow}</span>
          </p>
          <h1>{data.title}</h1>
          <p className="standfirst">{data.standfirst}</p>
        </div>
      </header>

      <section data-testid="visit-shape">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>{data.shape.eyebrow}</span>
          </p>
          <h2>{data.shape.heading}</h2>
          <div style={{ marginTop: "2rem" }}>
            {data.shape.steps.map((s) => (
              <div className="step" key={s.n} data-testid={`visit-step-${s.n}`}>
                <span className="n">{s.n}</span>
                <div>
                  <h3>{s.name}</h3>
                  <p>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ScatterBand />

      <section data-testid="visit-kinds">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>Which one to come to</span>
          </p>
          <h2>Four kinds of evening.</h2>
          <div style={{ marginTop: "1.8rem" }}>
            {data.kinds.map((k) => (
              <div className="practice" key={k.key} data-testid={`visit-kind-${k.key}`}>
                <div
                  className={`practice-icon ${ICON_CLASS[k.icon] || ""}`}
                  aria-hidden="true"
                >
                  <i />
                </div>
                <div>
                  <h3>{k.name}</h3>
                  <p style={{ color: "var(--ink)", fontStyle: "italic" }}>
                    {k.one_liner}
                  </p>
                  <p style={{ marginTop: ".5rem" }}>{k.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Rule />

      <section data-testid="visit-faq">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>The questions people actually ask</span>
          </p>
          <h2>Fair questions.</h2>
          <div style={{ marginTop: "2rem" }} data-testid="visit-faq-accordion">
            {data.faq.map((item, i) => (
              <details
                className="qa"
                key={item.q || i}
                data-testid={`visit-faq-item-${i + 1}`}
              >
                <summary>{item.q}</summary>
                <p className="a">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Rule />

      <section className="cta-block" data-testid="visit-cta">
        <div className="prose">
          <h2>Still reading?</h2>
          <p>
            Then you should probably just come. There&rsquo;s nothing to sign and
            no application &mdash; come once, see whether the room is any good,
            come again.
          </p>
          <div className="actions" style={{ justifyContent: "center" }}>
            <Link to="/gatherings" className="button" data-testid="visit-gatherings-cta">
              See what&rsquo;s on
            </Link>
            <Link to="/join" className="linkish" data-testid="visit-join-cta">
              Or just write to us &rarr;
            </Link>
          </div>
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
